import { AutoComplete, Spin, Tag, Typography } from 'antd';
import { Constants } from 'utils/constants';
import styles from './RegionAutocomplete.module.scss';

/**
 *
 * @component
 *
 * @summary RegionAutocomplete component
 *
 * @description
 * A reusable autocomplete input that allows searching, selecting,
 * and displaying region-based filters with tags.
 * Includes a loading spinner while fetching data and a message when
 * no results are found.
 *
 * @param {Object} props - Component props object.
 * @param {boolean} [props.disabled = false] - Autocomplete disabled.
 * @param {Object} props.query - Query object that provides autocomplete data and status flags.
 * @param {Array} [props.query.data] - List of region objects with `label` and `code` keys.
 * @param {boolean} [props.query.isFetching] - Whether the query is currently fetching data.
 * @param {boolean} [props.query.isEmpty] - Whether the query returned no results.
 * @param {Object} props.filters - Current filter state containing both input value and selected items.
 * @param {Function} props.setFilters - Dispatch-like function to update the filter state.
 * @param {string} props.placeholder - Placeholder text for the autocomplete input.
 * @param {string} props.searchType - Key in `filters` for the search input value.
 * @param {string} props.selectedType - Key in `filters` for the selected items array.
 * @param {string} props.searchTypeAction - Action type string for updating the search input.
 * @param {string} props.selectedTypeAction - Action type string for updating selected items.
 * @param {(number) => void} props.selectedTypeAction - Removing item functionality.
 * @returns {JSX.Element} The rendered RegionAutocomplete component.
 *
 * @example
 * ```jsx
 * <RegionAutocomplete
 *   disabled={false}
 *   query={regionHQQuery}
 *   filters={filters}
 *   setFilters={setFilters}
 *   placeholder="Αναζήτησε Περιφέρεια Έδρας"
 *   searchType="regionHQ"
 *   selectedType="selectedRegionHQ"
 *   searchTypeAction="SET_REGION_HQ"
 *   selectedTypeAction="SET_SELECTED_REGION_HQ"
 *   onDeleteItem={handleDeleteRegion}
 * />
 * ```
 */
export function RegionAutocomplete(props) {
  const {
    disabled = false,
    query,
    filters,
    setFilters,
    placeholder,
    searchType,
    selectedType,
    searchTypeAction,
    selectedTypeAction,
    onDeleteItem,
  } = props;

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <AutoComplete
          disabled={disabled}
          className={styles.autocomplete}
          maxLength={30}
          placeholder={placeholder}
          value={filters[searchType]}
          options={
            query.data?.map(item => ({
              ...item,
              value: item.label,
            })) ?? []
          }
          onSearch={value => {
            setFilters({ type: searchTypeAction, value });
          }}
          onSelect={(_, option) => {
            if (
              filters[selectedType].some(
                selected => selected.code === option.code
              )
            ) {
              return;
            }
            setFilters({
              type: selectedTypeAction,
              value: [...filters[selectedType], option],
            });
            setFilters({ type: searchTypeAction, value: '' });
          }}
        />

        {/* Fetching state */}
        {query.isFetching && (
          <div className={styles.spinner}>
            <Spin size="small" />
          </div>
        )}
      </div>

      {/* Empty state message */}
      {query.isEmpty && (
        <div className={styles.empty}>
          <Typography.Text type="secondary">
            {Constants.NoDataFound}
          </Typography.Text>
        </div>
      )}

      <div className={styles.tags}>
        {filters[selectedType].map(item => (
          <Tag
            key={item.code.toString()}
            closable
            onClose={() => onDeleteItem(item.code)}
          >
            {item.label}
          </Tag>
        ))}
      </div>
    </div>
  );
}
